/**
 * The TileSet data model shared by the tile node slices, this slice's decode and the placement
 * and geometry math, free of React and THREE. It carries `tileDrawInfo` beside the shapes, since a
 * consumer holding the types needs it to read the data.
 */
import { TILE_TRANSFORM_FLIP_H, TILE_TRANSFORM_FLIP_V, TILE_TRANSFORM_TRANSPOSE, } from '../../godot/scene/tileSet';
const TILE_TRANSFORM_MASK = TILE_TRANSFORM_FLIP_H | TILE_TRANSFORM_FLIP_V | TILE_TRANSFORM_TRANSPOSE;
/** The authored alternative a cell's id names, its transform bits cleared (`tile_set.cpp:5320-5322`). */
export function alternativeNoTransform(alternativeId) {
    return alternativeId & ~TILE_TRANSFORM_MASK;
}
/** A tile's pixel rect in the atlas image, frame 0 (`get_tile_texture_region`, `tile_set.cpp:5283-5296`). */
function atlasTileRegion(source, atlasCoords, sizeInAtlas) {
    const { margins, separation, textureRegionSize: region } = source;
    return {
        x: margins.x + atlasCoords.x * (region.x + separation.x),
        y: margins.y + atlasCoords.y * (region.y + separation.y),
        width: region.x * sizeInAtlas.x + separation.x * (sizeInAtlas.x - 1),
        height: region.y * sizeInAtlas.y + separation.y * (sizeInAtlas.y - 1),
    };
}
/**
 * A drawable cell's draw info. The cell's transform bits compose with its tile's own, transposed
 * first, and move the texture origin with them, as `compute_transformed_tile_dest_rect` does
 * (`tile_map_layer.cpp:2727-2774`).
 */
export function tileDrawInfo(source, cell) {
    const { atlasCoords, alternativeId, tile, tileData } = cell;
    const cellTranspose = (alternativeId & TILE_TRANSFORM_TRANSPOSE) !== 0;
    const cellFlipH = (alternativeId & TILE_TRANSFORM_FLIP_H) !== 0;
    const cellFlipV = (alternativeId & TILE_TRANSFORM_FLIP_V) !== 0;
    const { textureOrigin } = tileData;
    const origin = cellTranspose ? { x: textureOrigin.y, y: textureOrigin.x } : textureOrigin;
    return {
        regionPx: atlasTileRegion(source, atlasCoords, tile.sizeInAtlas),
        orientation: {
            // A cell transpose swaps the axes the tile's own flips act on.
            flipH: cellFlipH !== (cellTranspose ? tileData.flipV : tileData.flipH),
            flipV: cellFlipV !== (cellTranspose ? tileData.flipH : tileData.flipV),
            transpose: tileData.transpose !== cellTranspose,
        },
        centreOffset: {
            x: cellFlipH ? origin.x : -origin.x,
            y: cellFlipV ? origin.y : -origin.y,
        },
    };
}
/** `TileData`'s own defaults (tile_set.h:851-858), as a fresh object a decode may fill. */
export function defaultTileData() {
    return {
        flipH: false,
        flipV: false,
        transpose: false,
        textureOrigin: { x: 0, y: 0 },
        modulate: { r: 1, g: 1, b: 1, a: 1 },
        material: null,
        zIndex: 0,
        ySortOrigin: 0,
    };
}
//# sourceMappingURL=types.js.map